
import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Loader2, CheckCircle2, XCircle, Info } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

const AdminSetupTool = () => {
  const [isRunning, setIsRunning] = useState(false);
  const [result, setResult] = useState<any>(null);
  const { toast } = useToast();

  const runSetup = async () => {
    setIsRunning(true);
    
    try {
      // Call the Edge Function we just created
      const { data, error } = await supabase.functions.invoke('admin-setup');
      
      if (error) {
        throw new Error(error.message);
      }
      
      setResult(data);
      
      toast({
        title: data.success ? "Setup erfolgreich" : "Setup teilweise fehlgeschlagen",
        description: data.message,
        variant: data.success ? "default" : "destructive",
      });
    } catch (error) {
      console.error("Setup error:", error);
      toast({
        title: "Setup fehlgeschlagen",
        description: error.message || "Ein unbekannter Fehler ist aufgetreten",
        variant: "destructive",
      });
    } finally {
      setIsRunning(false);
    }
  };

  const renderStatus = (status: string) => {
    switch (status) {
      case 'created':
        return <span className="text-green-600 flex items-center gap-1"><CheckCircle2 size={16} /> Erstellt</span>;
      case 'updated':
        return <span className="text-blue-600 flex items-center gap-1"><Info size={16} /> Aktualisiert</span>;
      case 'exists':
        return <span className="text-gray-600 flex items-center gap-1"><Info size={16} /> Existiert bereits</span>;
      case 'linked':
        return <span className="text-green-600 flex items-center gap-1"><CheckCircle2 size={16} /> Verknüpft</span>;
      case 'error':
        return <span className="text-red-600 flex items-center gap-1"><XCircle size={16} /> Fehler</span>;
      default:
        return <span className="text-gray-600">{status}</span>;
    }
  };

  return (
    <Card className="max-w-4xl mx-auto mt-12">
      <CardHeader>
        <CardTitle className="text-xl">CHRONA Admin Setup Tool</CardTitle>
        <CardDescription>
          Dieses Tool erstellt die angegebenen Benutzerkonten und konfiguriert die Beziehungen.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {!result ? (
          <div className="p-6 text-center">
            <p className="mb-6 text-gray-700">
              Das Tool wird folgende Aktionen durchführen:
            </p>
            <ul className="text-left list-disc pl-6 mb-6 space-y-2">
              <li>Prüfen und ggf. anlegen: <strong>support@chrona.it</strong> mit Rolle <code>admin</code></li>
              <li>Prüfen und ggf. anlegen: <strong>kunde@chrona.app</strong> mit Rolle <code>customer</code></li>
              <li>Prüfen und ggf. anlegen: <strong>studio@chrona.app</strong> mit Rolle <code>studio_owner</code></li>
              <li>Verknüpfen: Studio "Berlin Hair Studio" mit Demo-Studio-User</li>
            </ul>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Results */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Admin User */}
              <div className="border p-4 rounded-md">
                <h3 className="font-medium mb-2 flex items-center justify-between">
                  <span>Admin User</span>
                  {result.adminUser && renderStatus(result.adminUser.status)}
                </h3>
                <p className="text-sm text-gray-600">Email: support@chrona.it</p>
                {result.adminUser?.message && (
                  <p className="text-sm text-red-600 mt-2">{result.adminUser.message}</p>
                )}
              </div>

              {/* Customer User */}
              <div className="border p-4 rounded-md">
                <h3 className="font-medium mb-2 flex items-center justify-between">
                  <span>Customer User</span>
                  {result.customerUser && renderStatus(result.customerUser.status)}
                </h3>
                <p className="text-sm text-gray-600">Email: kunde@chrona.app</p>
                {result.customerUser?.message && (
                  <p className="text-sm text-red-600 mt-2">{result.customerUser.message}</p>
                )}
              </div>

              {/* Studio Owner User */}
              <div className="border p-4 rounded-md">
                <h3 className="font-medium mb-2 flex items-center justify-between">
                  <span>Studio Owner User</span>
                  {result.studioOwnerUser && renderStatus(result.studioOwnerUser.status)}
                </h3>
                <p className="text-sm text-gray-600">Email: studio@chrona.app</p>
                {result.studioOwnerUser?.message && (
                  <p className="text-sm text-red-600 mt-2">{result.studioOwnerUser.message}</p>
                )}
              </div>

              {/* Studio Linking */}
              <div className="border p-4 rounded-md">
                <h3 className="font-medium mb-2 flex items-center justify-between">
                  <span>Studio Verknüpfung</span>
                  {result.studioLinking && renderStatus(result.studioLinking.status)}
                </h3>
                <p className="text-sm text-gray-600">Berlin Hair Studio → studio@chrona.app</p>
                {result.studioLinking?.message && (
                  <p className="text-sm text-red-600 mt-2">{result.studioLinking.message}</p>
                )}
              </div>
            </div>

            {/* Login Information */}
            {result.loginInfo && (
              <Alert className="mt-4">
                <AlertTitle>Login Informationen</AlertTitle>
                <AlertDescription className="space-y-2">
                  <p><strong>Passwort für alle Accounts:</strong> {result.loginInfo.password}</p>
                  <p><strong>Passwort-Reset URL:</strong> {result.loginInfo.passwordReset}</p>
                </AlertDescription>
              </Alert>
            )}

            {/* User Details Collapsible */}
            <details className="mt-4">
              <summary className="cursor-pointer font-medium">Alle Nutzer als JSON (klicken zum Anzeigen)</summary>
              <pre className="bg-gray-100 p-4 rounded text-xs mt-2 overflow-auto max-h-96">
                {JSON.stringify(result.allUsers, null, 2)}
              </pre>
            </details>
          </div>
        )}
      </CardContent>
      <CardFooter className="flex justify-center">
        <Button 
          onClick={runSetup} 
          disabled={isRunning}
          variant={result ? "outline" : "default"}
          size="lg"
        >
          {isRunning ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Setup wird ausgeführt...
            </>
          ) : result ? (
            "Setup erneut ausführen"
          ) : (
            "Setup starten"
          )}
        </Button>
      </CardFooter>
    </Card>
  );
};

export default AdminSetupTool;
